/**
 * InsertVideoDialog — adds a video to a content section.
 *
 * Two tabs:
 *   • Upload an MP4 (H.264) up to 500 MB
 *   • Paste a YouTube or Vimeo URL (no upload, embeds via iframe)
 *
 * The upload flow:
 *   1. Browser pre-extracts a poster frame (canvas capture at t=1s) and the
 *      duration — small but nice quality-of-life win, saves a server-side
 *      ffmpeg pipeline. Best-effort: silently skipped if the metadata isn't
 *      available yet.
 *   2. Browser uploads the MP4 *directly* to Tigris via a backend-issued
 *      presigned PUT URL (`uploadVideoDirect`). FastAPI never sees the body —
 *      this dodges Nginx/CDN body-size caps and uvicorn temp-disk pressure.
 *   3. Browser POSTs the resulting file_id (+ poster dataURL + duration) to
 *      /api/v1/videos/sections/{sectionId}/attach, which creates the slide.
 *
 * The URL embed flow skips step 1+2 and goes straight to attach.
 */
import { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Progress } from '../ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Upload, Link as LinkIcon, Loader2, Film } from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import { uploadVideoDirect, UploadCancelledError, formatSpeed, formatEta, formatBytes } from '../../utils/uploadVideoDirect';

const MAX_MB = 2048;

/** Extract a JPEG poster frame at t≈1s + duration from an MP4. Returns
 *  { posterDataUrl, durationSeconds } or {} if the browser couldn't load it. */
async function probeVideoMetadata(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.muted = true;
    v.src = url;
    let done = false;
    const cleanup = () => {
      // Properly release the file from the media element BEFORE revoking the
      // blob URL. Chrome can otherwise keep reading the file via the video
      // pipeline for hundreds of ms after we resolve — which races with the
      // FormData upload that immediately follows and surfaces as a silent
      // `xhr.onerror` (status 0, no body). WhatsApp MP4s are especially
      // affected because their `moov` atom sits at the end of the file, so
      // metadata extraction has to scan the whole thing.
      try { v.pause(); } catch { /* element may not have started */ }
      v.removeAttribute('src');
      try { v.load(); } catch { /* harmless if no source attached */ }
      URL.revokeObjectURL(url);
    };
    const finish = (result) => {
      if (done) return;
      done = true;
      cleanup();
      resolve(result);
    };
    // Hard timeout — some browsers stall on weird codecs
    const tid = setTimeout(() => finish({}), 8000);

    v.addEventListener('loadedmetadata', () => {
      // Seek to 1s (or 0 for very short videos) so the poster isn't a black frame
      v.currentTime = Math.min(1, (v.duration || 1) / 2);
    });
    v.addEventListener('seeked', () => {
      try {
        const canvas = document.createElement('canvas');
        // Cap poster resolution at 640 wide to keep base64 payload small
        const ratio = (v.videoHeight || 360) / (v.videoWidth || 640);
        canvas.width = Math.min(640, v.videoWidth || 640);
        canvas.height = Math.round(canvas.width * ratio);
        const ctx = canvas.getContext('2d');
        ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
        const posterDataUrl = canvas.toDataURL('image/jpeg', 0.7);
        clearTimeout(tid);
        finish({ posterDataUrl, durationSeconds: v.duration || null });
      } catch {
        clearTimeout(tid);
        finish({ durationSeconds: v.duration || null });
      }
    });
    v.addEventListener('error', () => { clearTimeout(tid); finish({}); });
  });
}

export default function InsertVideoDialog({
  open, onClose, sectionId, onAdded,
  // Optional — when provided, the dialog shows a position selector so the
  // trainer can pick exactly where the new video lands inside the section.
  // `defaultPosition` pre-selects a slot (e.g. for the Replace flow, callers
  // pass the OLD slide's `order` so the new one lands in the same place).
  existingSlides = [],
  defaultPosition = null,
}) {
  const api = useApi();
  const { post } = api;
  const [tab, setTab] = useState('upload');
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState('');
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState('');
  // Rich transfer stats — populated by uploadVideoDirect's onStats callback.
  // Null fields mean "not yet known" (first ~3s before the speed tracker
  // has enough samples). The UI renders em-dashes for null values.
  const [stats, setStats] = useState({
    percent: 0, loadedBytes: 0, totalBytes: 0, speedBps: 0, etaSeconds: null,
  });
  // AbortController for the in-flight XHR PUT. Lives in a ref so the Cancel
  // button can call .abort() without re-rendering, and we can recreate it
  // for each fresh upload attempt.
  const abortRef = useRef(null);
  // Insert position — `null` = end (default, current behaviour). Otherwise a
  // 0-indexed slot. Reset every time the dialog opens so a previous selection
  // doesn't leak into a new attach.
  const [position, setPosition] = useState(defaultPosition);
  useEffect(() => {
    if (open) setPosition(defaultPosition);
  }, [open, defaultPosition]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      // Reset every time the dialog reopens — keeps state predictable.
      setTab('upload'); setFile(null); setCaption(''); setUrl('');
      setBusy(false); setPhase('');
      setStats({ percent: 0, loadedBytes: 0, totalBytes: 0, speedBps: 0, etaSeconds: null });
      abortRef.current = null;
    }
  }, [open]);

  const handleFilePick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.mp4')) {
      toast.error('Only MP4 files are supported. Please convert before uploading.');
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`File too large. Max ${MAX_MB} MB.`);
      return;
    }
    setFile(f);
  };

  const handleCancel = () => {
    // Best-effort abort — the XHR's onabort handler will reject the
    // upload promise with UploadCancelledError, which the catch swallows.
    abortRef.current?.abort();
  };

  const handleUpload = async () => {
    if (!file) return;
    setBusy(true);
    setStats({ percent: 0, loadedBytes: 0, totalBytes: file.size, speedBps: 0, etaSeconds: null });
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      setPhase('Reading video metadata…');
      const meta = await probeVideoMetadata(file);
      // Yield one macrotask so Chrome's media pipeline fully releases the
      // file blob before we hand it to the XHR PUT body. Without this,
      // larger MP4s (especially WhatsApp clips with trailing `moov` atoms)
      // fail mid-upload because the video element is still draining the
      // same File object. See probeVideoMetadata cleanup comment for the
      // underlying Chrome behaviour.
      await new Promise((r) => setTimeout(r, 0));

      // Direct browser → Tigris upload via presigned PUT. The MP4 body
      // never touches FastAPI — backend just signs the URL and verifies
      // the resulting object via HEAD.
      const uploadRes = await uploadVideoDirect({
        file,
        api,
        signal: ctrl.signal,
        onPhase: setPhase,
        onStats: setStats,
      });

      setPhase('Creating slide…');
      await post(`/videos/sections/${sectionId}/attach`, {
        source: 'upload',
        file_id: uploadRes.file_id,
        duration_seconds: meta.durationSeconds || undefined,
        poster_dataurl: meta.posterDataUrl || undefined,
        caption: caption || undefined,
        title: file.name.replace(/\.mp4$/i, '').slice(0, 200),
        position: position == null ? undefined : position,
      });
      toast.success('Video added to section');
      onAdded?.();
      onClose?.();
    } catch (e) {
      // Cancellation is a normal user action, not an error — show an info
      // toast and bail without the red error styling.
      if (e instanceof UploadCancelledError || e?.cancelled) {
        toast.info('Upload cancelled.');
      } else {
        console.error('[video-upload] handleUpload failed', e, {
          size: file?.size, name: file?.name, type: file?.type,
        });
        toast.error(e.message || e.response?.data?.detail || 'Video upload failed');
      }
    } finally {
      setBusy(false);
      setPhase('');
      setStats({ percent: 0, loadedBytes: 0, totalBytes: 0, speedBps: 0, etaSeconds: null });
      abortRef.current = null;
    }
  };

  // Position picker — only render when the section already has slides.
  // Hidden for an empty section since "at end" is the only possible slot.
  const renderPositionPicker = () => {
    if (!existingSlides || existingSlides.length === 0) return null;
    const slotValue = position == null ? '__end__' : String(position);
    return (
      <div className="space-y-1.5" data-testid="video-position-picker">
        <Label htmlFor="video-position">Insert position</Label>
        <select
          id="video-position"
          value={slotValue}
          onChange={(e) => {
            const v = e.target.value;
            setPosition(v === '__end__' ? null : parseInt(v, 10));
          }}
          disabled={busy}
          className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm
                     focus:outline-none focus:ring-2 focus:ring-ring"
          data-testid="video-position-select"
        >
          {/* Slot 0 = before slide 1. Slot N (1..count) = after slide N. */}
          <option value="0">At the start (before slide 1)</option>
          {existingSlides.map((sl, i) => (
            <option key={sl.slide_id} value={String(i + 1)}>
              {`After slide ${i + 1}${sl.title ? ` — ${sl.title.slice(0, 40)}` : ''}`}
            </option>
          ))}
          <option value="__end__">At the end (default)</option>
        </select>
      </div>
    );
  };

  const handleEmbed = async () => {
    if (!url.trim()) return;
    const lower = url.toLowerCase();
    let source = null;
    if (lower.includes('youtube.com') || lower.includes('youtu.be')) source = 'youtube';
    else if (lower.includes('vimeo.com')) source = 'vimeo';
    if (!source) {
      toast.error('Only YouTube and Vimeo URLs are supported.');
      return;
    }
    setBusy(true);
    try {
      await post(`/videos/sections/${sectionId}/attach`, {
        source,
        url: url.trim(),
        caption: caption || undefined,
        position: position == null ? undefined : position,
      });
      toast.success(`${source === 'youtube' ? 'YouTube' : 'Vimeo'} video added`);
      onAdded?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Embed failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose?.()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="insert-video-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Film className="w-5 h-5" /> Add video</DialogTitle>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2">
            <TabsTrigger value="upload" disabled={busy} data-testid="tab-upload">
              <Upload className="w-3.5 h-3.5 mr-1.5" /> Upload MP4
            </TabsTrigger>
            <TabsTrigger value="url" disabled={busy} data-testid="tab-url">
              <LinkIcon className="w-3.5 h-3.5 mr-1.5" /> YouTube / Vimeo
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upload" className="space-y-3 mt-3">
            <div
              className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:bg-muted/30 transition-colors"
              onClick={() => fileInputRef.current?.click()}
              data-testid="video-drop-zone"
            >
              <Film className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              {file ? (
                <>
                  <p className="text-sm font-medium">{file.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatBytes(file.size)}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium">Drop an MP4 here or click to browse</p>
                  <p className="text-xs text-muted-foreground">
                    MP4 (H.264 + AAC) · max {(MAX_MB / 1024).toFixed(0)} GB
                  </p>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="video/mp4,.mp4"
                onChange={handleFilePick}
                className="hidden"
                data-testid="video-file-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="video-caption">Caption (optional)</Label>
              <Input
                id="video-caption"
                value={caption}
                maxLength={160}
                placeholder="e.g. Onboarding day-1 welcome message"
                onChange={(e) => setCaption(e.target.value)}
                disabled={busy}
                data-testid="video-caption-input"
              />
            </div>
            {renderPositionPicker()}
            {busy && (
              <div className="space-y-2 rounded-md border bg-muted/30 p-3" data-testid="upload-progress-card">
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-xs font-medium text-foreground">{phase}</p>
                  {stats.totalBytes > 0 && (
                    <p className="text-xs tabular-nums text-muted-foreground" data-testid="upload-progress-percent">
                      {stats.percent}%
                    </p>
                  )}
                </div>
                {stats.percent > 0 && stats.percent < 100 && (
                  <Progress value={stats.percent} data-testid="upload-progress" />
                )}
                {stats.totalBytes > 0 && (
                  <div className="flex items-center justify-between gap-3 text-xs tabular-nums text-muted-foreground">
                    <span data-testid="upload-bytes">
                      {formatBytes(stats.loadedBytes)} / {formatBytes(stats.totalBytes)}
                    </span>
                    <span data-testid="upload-speed">{formatSpeed(stats.speedBps)}</span>
                    <span data-testid="upload-eta">
                      ETA {formatEta(stats.etaSeconds)}
                    </span>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="url" className="space-y-3 mt-3">
            <div className="space-y-1.5">
              <Label htmlFor="video-url">YouTube or Vimeo URL</Label>
              <Input
                id="video-url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=... or https://vimeo.com/..."
                disabled={busy}
                data-testid="video-url-input"
              />
              <p className="text-xs text-muted-foreground">
                Heads up: embedded videos require participants&apos; devices to have internet during playback.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="video-caption-2">Caption (optional)</Label>
              <Input
                id="video-caption-2"
                value={caption}
                maxLength={160}
                placeholder="e.g. Vendor compliance training"
                onChange={(e) => setCaption(e.target.value)}
                disabled={busy}
              />
            </div>
            {renderPositionPicker()}
          </TabsContent>
        </Tabs>

        <DialogFooter>
          {/* When mid-upload, the left "Cancel" button aborts the in-flight
              PUT (via AbortController) instead of closing the dialog. Closing
              an in-progress upload would orphan a partial Tigris object. */}
          {busy && tab === 'upload' ? (
            <Button
              variant="outline"
              onClick={handleCancel}
              data-testid="video-upload-cancel"
            >
              Cancel upload
            </Button>
          ) : (
            <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          )}
          {tab === 'upload' ? (
            <Button
              onClick={handleUpload}
              disabled={busy || !file}
              data-testid="video-upload-confirm"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
              Upload &amp; add
            </Button>
          ) : (
            <Button
              onClick={handleEmbed}
              disabled={busy || !url.trim()}
              data-testid="video-embed-confirm"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <LinkIcon className="w-4 h-4 mr-1.5" />}
              Add embed
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
